import { useState, useCallback, useEffect } from 'react';
import { sendMessage } from '../services/prd-agent';
import {
    listConversations,
    getConversation as fetchConversation,
    createConversation as apiCreateConversation,
    updateConversation as apiUpdateConversation,
    deleteConversation as apiDeleteConversation,
} from '../services/conversation-api';
import { createArtifact, getArtifact, updateArtifact } from '../services/artifact-api';
import { lockForArtifactStatus, lockForCode, UNLOCKED } from './conversationLock';
import { LockError } from '../services/lock-error';

function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export default function usePrdAgent() {
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [error, setError] = useState(null);
    const [activeArtifact, setActiveArtifact] = useState(null);
    // Derived lock on the active conversation: { locked, code, reason, terminal }.
    // Sources, in order of authority: the 409 the backend returns on a locked
    // write (reactive), the governing artifact status read on load (proactive),
    // and an optimistic lock set the moment a submit-for-approval succeeds.
    const [lockState, setLockState] = useState(UNLOCKED);

    useEffect(() => {
        refreshList({ withSpinner: true });
    }, []);

    const refreshList = async ({ withSpinner = false } = {}) => {
        if (withSpinner) setIsLoadingList(true);
        try {
            const result = await listConversations();
            setConversations(result.conversations);
        } catch (err) {
            console.error('Failed to load conversations:', err);
        } finally {
            if (withSpinner) setIsLoadingList(false);
        }
    };

    const startNew = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const { sessionId, title, createdAt } = await apiCreateConversation();
            const conv = {
                sessionId,
                title,
                messages: [],
                status: 'active',
                createdAt,
                updatedAt: createdAt,
            };
            setActiveConversation(conv);
            setActiveArtifact(null);
            setLockState(UNLOCKED);
            await refreshList();
        } catch (err) {
            setError(err.message || 'Failed to create conversation');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const resume = useCallback(async (sessionId) => {
        setIsLoadingConversation(true);
        setIsLoading(true);
        setActiveConversation(null);
        setActiveArtifact(null);
        setError(null);
        try {
            const detail = await fetchConversation(sessionId);
            const messages = detail.messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: m.timestamp,
                artifactId: m.artifactId,
                version: m.version,
                kind: m.kind,
            }));

            const artifactMessages = messages.filter((m) => m.artifactId);
            if (artifactMessages.length > 0) {
                const hydrations = artifactMessages.map(async (msg) => {
                    try {
                        // Hydrate the version this card rendered (msg.version).
                        // Legacy messages have no version → falls back to
                        // currentVersion (prior behavior).
                        const art = await getArtifact(msg.artifactId, msg.version);
                        msg.artifact = {
                            id: art.artifactId,
                            title: art.title,
                            fileName: art.fileName,
                            content: art.content,
                            mimeType: art.mimeType,
                            generatedAt: new Date(art.createdAt).toISOString(),
                            version: art.version,
                            currentVersion: art.currentVersion,
                        };
                    } catch (err) {
                        console.error(`Failed to hydrate artifact ${msg.artifactId}:`, err);
                    }
                });
                await Promise.all(hydrations);
            }

            const conv = {
                sessionId: detail.sessionId,
                title: detail.title,
                messages,
                status: detail.status,
                createdAt: detail.createdAt,
                updatedAt: detail.updatedAt,
            };
            setActiveConversation(conv);
            setActiveArtifact(null);

            // Proactive lock: derive it from the governing artifact's CURRENT
            // status so the composer is already blocked on entry (no need to
            // wait for the user to hit a 409). One PRD per conversation, so the
            // artifact behind any artifact message governs; we read it without a
            // version to get currentVersion's status. Reads always succeed even
            // when locked, so this never fails on a blocked conversation.
            //
            // Legacy belt-and-suspenders: a rejected conversation also carries
            // conversation.status === 'blocked'. If we can't read the artifact
            // status, fall back to that flag as a rejected lock.
            const governingId = messages.find((m) => m.artifactId)?.artifactId;
            if (governingId) {
                try {
                    const gov = await getArtifact(governingId);
                    setLockState(lockForArtifactStatus(gov.status));
                } catch (err) {
                    console.error('Failed to resolve lock from artifact:', err);
                    setLockState(
                        detail.status === 'blocked'
                            ? lockForCode('locked_rejected')
                            : UNLOCKED,
                    );
                }
            } else {
                setLockState(detail.status === 'blocked' ? lockForCode('locked_rejected') : UNLOCKED);
            }
        } catch (err) {
            setError(err.message || 'Failed to load conversation');
        } finally {
            setIsLoading(false);
            setIsLoadingConversation(false);
        }
    }, []);

    const remove = useCallback(
        async (sessionId) => {
            try {
                await apiDeleteConversation(sessionId);
                if (activeConversation && activeConversation.sessionId === sessionId) {
                    setActiveConversation(null);
                    setActiveArtifact(null);
                }
                await refreshList();
            } catch (err) {
                setError(err.message || 'Failed to delete conversation');
            }
        },
        [activeConversation],
    );

    const openArtifact = useCallback((artifact) => {
        setActiveArtifact(artifact);
    }, []);

    const closeArtifact = useCallback(() => {
        setActiveArtifact(null);
    }, []);

    const send = useCallback(
        async (userMessage) => {
            if (!activeConversation) return;
            // Never allow a send while terminally locked (approved/rejected).
            // The reversible pending_approval lock is also blocked here, but the
            // UI reopens it once the artifact moves to changes_requested (see
            // the artifact refresh after a reload). Guard is defensive — the
            // composer is already hidden when locked.
            if (lockState.locked) return;

            setError(null);
            setIsStreaming(true);

            const userMsg = {
                id: uuid(),
                role: 'user',
                content: userMessage,
                timestamp: Date.now(),
            };

            const assistantMsg = {
                id: uuid(),
                role: 'assistant',
                content: '',
                timestamp: Date.now(),
            };

            const updated = {
                ...activeConversation,
                messages: [...activeConversation.messages, userMsg, assistantMsg],
                updatedAt: Date.now(),
            };

            setActiveConversation(updated);

            const pendingArtifacts = [];

            await sendMessage(userMessage, activeConversation.sessionId, {
                onChunk: (text) => {
                    assistantMsg.content += text;
                    setActiveConversation((prev) =>
                        prev ? { ...prev, messages: [...prev.messages] } : prev,
                    );
                },
                onArtifact: (artifact) => {
                    const savePromise = (async () => {
                        let artifactId;
                        let version;

                        // Check if an artifact already exists in this conversation
                        const existingArtifactId = updated.messages.find(m => m.artifactId)?.artifactId;

                        try {
                            if (existingArtifactId) {
                                // Already exists → update (new version)
                                const result = await updateArtifact(existingArtifactId, {
                                    content: artifact.content,
                                    title: artifact.title,
                                });
                                artifactId = result.artifactId;
                                version = result.version;
                            } else {
                                // Doesn't exist → create (v1)
                                const saved = await createArtifact({
                                    sessionId: activeConversation.sessionId,
                                    title: artifact.title,
                                    fileName: artifact.fileName,
                                    content: artifact.content,
                                    mimeType: artifact.mimeType,
                                });
                                artifactId = saved.artifactId;
                                version = 1;
                            }
                        } catch (err) {
                            console.error('Failed to save artifact:', err);
                        }

                        const artifactMsg = {
                            id: uuid(),
                            role: 'assistant',
                            content: '',
                            timestamp: Date.now(),
                            artifact: { ...artifact, id: artifactId, version: version, currentVersion: version },
                            artifactId,
                            version,
                        };
                        updated.messages.push(artifactMsg);
                        setActiveConversation({ ...updated, messages: [...updated.messages] });
                        setActiveArtifact({ ...artifact, id: artifactId, version: version, currentVersion: version });
                    })();

                    pendingArtifacts.push(savePromise);
                },
                onTitle: async (title) => {
                    updated.title = title;
                    setActiveConversation({ ...updated });
                    try {
                        await apiUpdateConversation(activeConversation.sessionId, { title });
                        await refreshList();
                    } catch (err) {
                        console.error('Failed to update title:', err);
                    }
                },
                onDone: async () => {
                    await Promise.all(pendingArtifacts);

                    if (assistantMsg.content === '' && !assistantMsg.artifact) {
                        const idx = updated.messages.indexOf(assistantMsg);
                        if (idx !== -1) updated.messages.splice(idx, 1);
                    }

                    try {
                        // Refresh from backend before persisting to avoid clobbering
                        // messages the backend may have injected (e.g. a manager
                        // comment appended atomically while we were streaming).
                        let backendMessages = [];
                        try {
                            const latest = await fetchConversation(activeConversation.sessionId);
                            backendMessages = latest.messages || [];
                        } catch (err) {
                            console.error('Failed to refresh conversation before persist:', err);
                        }

                        // Merge: keep any backend message whose id we don't already
                        // have locally (manager comments, etc.), preserving `kind`.
                        const localIds = new Set(updated.messages.map((m) => m.id));
                        const injected = backendMessages
                            .filter((m) => !localIds.has(m.id))
                            .map((m) => ({
                                id: m.id,
                                role: m.role,
                                content: m.content,
                                timestamp: m.timestamp,
                                artifactId: m.artifactId,
                                version: m.version,
                                kind: m.kind,
                            }));

                        if (injected.length > 0) {
                            // Insert injected messages in timestamp order.
                            updated.messages = [...updated.messages, ...injected]
                                .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
                        }

                        await apiUpdateConversation(activeConversation.sessionId, {
                            title: updated.title,
                            messages: updated.messages.map((m) => ({
                                id: m.id,
                                role: m.role,
                                content: m.content,
                                timestamp: m.timestamp,
                                artifactId: m.artifactId,
                                // Persist the version this card rendered so it
                                // reopens its own version on reload.
                                version: m.version ?? m.artifact?.version,
                                // Preserve the manager-comment marker so the
                                // backend keeps the kind on the message.
                                kind: m.kind,
                            })),
                        });
                    } catch (err) {
                        console.error('Failed to persist conversation:', err);
                    }

                    setActiveConversation({ ...updated });
                    setIsStreaming(false);
                    await refreshList();
                },
                onError: (err) => {
                    // A locked conversation answers 409 with a LockError. Turn
                    // it into lock state (banner + blocked composer) instead of
                    // a raw error, and drop the optimistic user/assistant
                    // bubbles we appended — the turn never happened.
                    if (err instanceof LockError && err.code) {
                        setLockState(lockForCode(err.code, err.reason));
                        setActiveConversation((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      messages: prev.messages.filter(
                                          (m) => m.id !== userMsg.id && m.id !== assistantMsg.id,
                                      ),
                                  }
                                : prev,
                        );
                    } else {
                        setError(typeof err === 'string' ? err : err.message || 'Error');
                    }
                    setIsStreaming(false);
                },
            });
        },
        [activeConversation, lockState],
    );

    // Optimistic lock the moment a submit-for-approval succeeds, so the composer
    // blocks immediately without waiting for the next write to hit a 409. The
    // backend 409 is the backstop if a stray write slips through. Called by the
    // sidebar, which owns the submit action.
    const lockOnSubmit = useCallback(() => {
        setLockState(lockForCode('locked_pending_approval'));
    }, []);

    return {
        conversations,
        activeConversation,
        isStreaming,
        isLoading,
        isLoadingConversation,
        isLoadingList,
        error,
        activeArtifact,
        lockState,
        send,
        startNew,
        resume,
        remove,
        openArtifact,
        closeArtifact,
        lockOnSubmit,
    };
}
